import React, {
  useImperativeHandle,
  forwardRef,
  useRef,
  useState,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { Row, Col, Button, Form, Modal, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import {
  createSession,
  updateSessionById,
  fetchPracticeTemplates,
  uploadImage,
  getAppointmentById,
  getAccountByPatientId,
  updateAppointmentStatusByType,
} from "../../api";
import NoteRenderer from '../Templates/NoteRenderer';
import { notesToPages } from '../Templates/TemplateBuilder';
import SyncToButton from '../SyncTo/SyncToButton';
import { getUserPreferences } from '../BootNav/userPreferences';
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { getBookingsReturnPath } from "../../utils/bookingsReturn";
import ImageAnnotator from "./EditImages";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { PatientFiles } from "../PatientsAccounts/PatientFiles";
import "../../Components/Communications/Comms.css";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import ConfirmModal from "../Confirm/ConfirmModal";
import { useConfirm } from "../Confirm/useConfirm";

const MediaCaptureSection = ({ mediaImages, setMediaImages, isActive, onSubmit, submitLoading, onExpand, isFullScreen }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startStream = async () => {
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setError(null);
    } catch (e) {
      setError(e.message || "Camera access failed");
    }
  };

  useEffect(() => {
    if (isActive) {
      startStream();
    } else {
      // ensure camera is off when tab not active
      stopStream();
    }
    return stopStream; // also stop on unmount
  }, [isActive, facingMode]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);

    // JPEG keeps size down vs PNG
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setMediaImages((prev) => [dataUrl, ...prev]);
  };

  const removePhoto = (idx) =>
    setMediaImages((prev) => prev.filter((_, i) => i !== idx));

  const flipCamera = () =>
    setFacingMode((m) => (m === "environment" ? "user" : "environment"));

  return (
    <div className="mt-3">
      <Row className="align-items-center mb-3">
        <Col>
          <p className="text-muted mb-0">
            Take one or more photos and they’ll be saved to this session.
          </p>
        </Col>
        <Col xs="auto" className="d-flex align-items-center gap-2">
          <OverlayTrigger placement="top" overlay={<Tooltip>{isFullScreen ? "Collapse" : "Expand"}</Tooltip>}>
            <Button variant="link" className="text-primary text-decoration-none p-0 px-2" onClick={onExpand} aria-label={isFullScreen ? "Collapse" : "Expand"}>
              <i className={`fa-solid ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
            </Button>
          </OverlayTrigger>
        </Col>
      </Row>

      {error ? (
        <Alert variant="warning" className="mb-3">
          Camera access failed ({error}). You can still upload from your
          gallery:
          <Form.Control
            className="mt-2"
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              files.forEach((file) => {
                const reader = new FileReader();
                reader.onload = () => {
                  setMediaImages((prev) => [reader.result, ...prev]);
                };
                reader.readAsDataURL(file);
              });
              e.target.value = "";
            }}
          />
        </Alert>
      ) : (
        <div
          className="rounded overflow-hidden shadow-sm mb-3"
          style={{ position: "relative" }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ width: "100%", height: "auto", background: "#000" }}
          />
          <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center gap-3 p-3">
            <OverlayTrigger placement="top" overlay={<Tooltip>Capture</Tooltip>}>
              <Button
                variant="light"
                className="rounded-circle"
                onClick={capturePhoto}
                aria-label="Capture photo"
              >
                <i className="fa-solid fa-camera" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Flip camera</Tooltip>}>
              <Button
                variant="light"
                className="rounded-circle"
                onClick={flipCamera}
                aria-label="Flip camera"
              >
                <i className="fa-solid fa-arrows-rotate" />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      )}

      {mediaImages.length > 0 && (
        <>
          <h6 className="text-primary">Captured ({mediaImages.length})</h6>
          <Row>
            {mediaImages.map((url, idx) => (
              <Col xs={4} sm={3} md={2} key={idx} className="mb-3">
                <div className="position-relative border rounded overflow-hidden">
                  <img
                    src={url}
                    alt={`snap ${idx + 1}`}
                    style={{ width: "100%", height: 100, objectFit: "cover", cursor: "pointer" }}
                    onClick={() => setLightboxUrl(url)}
                    title="Click to enlarge"
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 m-1 p-1"
                    onClick={() => removePhoto(idx)}
                    aria-label="Remove"
                    title="Remove"
                  >
                    ×
                  </Button>
                </div>
              </Col>
            ))}
          </Row>
        </>
      )}

      <Modal show={!!lightboxUrl} onHide={() => setLightboxUrl(null)} centered size="lg">
        <Modal.Header closeButton />
        <Modal.Body className="p-0 text-center bg-dark">
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt="Full size"
              style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

// --- Voice recorder hook (modal-driven) ---
const useVoiceRecorder = () => {
  const { transcript, resetTranscript, listening } = useSpeechRecognition();

  const start = () => {
    if (!SpeechRecognition.browserSupportsSpeechRecognition()) return;
    resetTranscript();
    SpeechRecognition.startListening({
      continuous: true,
      interimResults: true,
      language: "en-US",
    });
  };

  const stop = () => {
    SpeechRecognition.stopListening();
    return (transcript || "").trim();
  };

  const clear = () => resetTranscript();

  return { listening, transcript, start, stop, clear };
};

// --- Recorder Modal ---
const RecorderModal = ({ show, onClose, onDone, vr }) => {
  useEffect(() => {
    if (show) vr.clear();
  }, [show]);

  const handleStart = () => vr.start();
  const handleStop = () => vr.stop(); // we only stop; we don't "use" yet
  const handleDone = () => {
    const text = (vr.transcript || "").trim();
    if (vr.listening) vr.stop(); // safety: ensure stopped before closing
    onDone?.(text);
    onClose?.();
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">Voice Recorder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center gap-3">
          {/* Soundwave: animates only when vr.listening === true */}
          <div className={`soundwave ${vr.listening ? "live" : ""}`}>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div className="w-100">
            <Form.Label className="fw-semibold">Live transcript</Form.Label>
            <div
              style={{
                minHeight: 90,
                border: "1px solid var(--acc-border-color)",
                borderRadius: 8,
                padding: "0.5rem 0.75rem",
                background: "var(--acc-header-bg)",
                whiteSpace: "pre-wrap",
              }}
            >
              {vr.transcript || (
                <span className="text-muted">Say something…</span>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          {!vr.listening ? (
            <Button variant="primary" onClick={handleStart}>
              <i className="fa-light fa-microphone me-2" /> Start
            </Button>
          ) : (
            <Button variant="outline-danger" onClick={handleStop}>
              <i className="fa-light fa-stop me-2" /> Stop
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDone}
            disabled={!vr.transcript?.trim()}
          >
            Use Transcript
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// Template Section Component
const TemplateSection = ({
  sessionTemplate,
  onTemplateChange,
  dropdownOptions,
  templatePages,
  capturedValues,
  onCapturedValuesChange,
  onVoiceRequest,
  lastSaved,
  onExpand,
  isFullScreen,
  isMobile,
}) => {

  return (
    <>
      <Row className="align-items-center mb-4 mt-1">
        <Col xs={12} className="d-flex align-items-center gap-2 flex-nowrap" style={{ minWidth: 0 }}>
          <Form.Label className="mb-0 text-primary fw-bold flex-shrink-0">
            Template:
          </Form.Label>
          <Form.Select
            controlid="formGridSessionTemplate"
            className="flex-shrink-1"
            style={{ minWidth: 0, flex: "1 1 auto" }}
            value={sessionTemplate}
            onChange={(e) => onTemplateChange(e.target.value)}
          >
            <option value="" disabled>
              Choose a template...
            </option>
            {dropdownOptions.map((category, idx) => (
              <React.Fragment key={idx}>
                <option disabled className="text-primary fw-bold">
                  {category.label}
                </option>
                {category.options.map((template) => (
                  <option key={template._id} value={template._id}>
                    {template.templateTitle}
                  </option>
                ))}
              </React.Fragment>
            ))}
          </Form.Select>
          <OverlayTrigger placement="top" overlay={<Tooltip>{isFullScreen ? "Collapse" : "Expand"}</Tooltip>}>
            <Button variant="link" className="text-primary text-decoration-none p-0 px-2" onClick={onExpand} aria-label={isFullScreen ? "Collapse" : "Expand"}>
              <i className={`fa-solid ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
            </Button>
          </OverlayTrigger>
        </Col>
      </Row>

      {sessionTemplate && templatePages.length > 0 ? (
        <>
          {lastSaved && (
            <div className="text-muted small mb-2">
              Last saved: {lastSaved.toLocaleTimeString()}
            </div>
          )}
          <NoteRenderer
            mode="fill"
            pages={templatePages}
            values={capturedValues}
            onChange={onCapturedValuesChange}
            onVoiceRequest={onVoiceRequest}
          />
        </>
      ) : sessionTemplate ? (
        <p className="text-muted small">This template has no pages defined.</p>
      ) : null}
    </>
  );
};

// Handwriting Modal Component (fullscreen canvas for stylus/touch input)
const InlineWriteSection = forwardRef(({ onSave, existingImage, onExpand, isFullScreen }, ref) => {
  const canvasRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [isEraser, setIsEraser] = useState(false);
  const [isHighlighter, setIsHighlighter] = useState(false);

  const colorOptions = ["#000000", "#0f3490", "#dc3545", "#198754", "#6c757d"];
  const strokeOptions = [2, 3, 5, 8];

  // The page the live strokes sit on top of — the note's SAVED image, never a
  // composite this component produced.
  //
  // `capture` merges this background with the current strokes and hands the
  // result up, and that result comes straight back down as `existingImage`.
  // Feeding it into the canvas baked every stroke into the background while the
  // stroke's path was still on the canvas, so undo / redo / the eraser — which
  // only ever touch paths — looked dead: the line they removed was still there,
  // in the background behind them. Pinning the background keeps every capture a
  // plain merge of this image with whatever paths currently exist.
  const lastCaptureRef = useRef(null);
  const [canvasBg, setCanvasBg] = useState(existingImage || "");
  useEffect(() => {
    // Our own composite echoing back — leave the background alone.
    if (existingImage && existingImage === lastCaptureRef.current) return;
    setCanvasBg(existingImage || "");
  }, [existingImage]);

  const toggleEraser = () => {
    const next = !isEraser;
    setIsEraser(next);
    setIsHighlighter(false);
    canvasRef.current?.eraseMode(next);
  };

  const toggleHighlighter = () => {
    const next = !isHighlighter;
    setIsHighlighter(next);
    setIsEraser(false);
    canvasRef.current?.eraseMode(false);
    if (next) { setStrokeColor("rgba(255,255,0,0.4)"); setStrokeWidth(20); }
    else { setStrokeColor("#000000"); setStrokeWidth(3); }
  };

  // Merges current strokes with background and returns the composite image.
  // Does NOT clear the canvas so the user can keep editing after navigating away and back.
  const capture = async () => {
    if (!canvasRef.current) return canvasBg || null;
    try {
      const paths = await canvasRef.current.exportPaths();
      const hasNewStrokes = paths && paths.length > 0;
      // Nothing drawn — the saved page already IS the current image, and
      // exporting here would write a blank canvas over it.
      if (!hasNewStrokes) return canvasBg || null;
      const strokesDataUrl = await canvasRef.current.exportImage("png");
      let result;
      if (canvasBg) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const strokesImg = new Image();
        strokesImg.crossOrigin = "anonymous";
        await new Promise((res, rej) => { strokesImg.onload = res; strokesImg.onerror = rej; strokesImg.src = strokesDataUrl; });
        const bgImg = new Image();
        bgImg.crossOrigin = "anonymous";
        await new Promise((res, rej) => { bgImg.onload = res; bgImg.onerror = rej; bgImg.src = canvasBg; });
        canvas.width = strokesImg.width || 800;
        canvas.height = strokesImg.height || 600;
        const bgAspect = bgImg.width / bgImg.height;
        const canvasAspect = canvas.width / canvas.height;
        let dw, dh, ox, oy;
        if (bgAspect > canvasAspect) { dw = canvas.width; dh = canvas.width / bgAspect; ox = 0; oy = (canvas.height - dh) / 2; }
        else { dh = canvas.height; dw = canvas.height * bgAspect; ox = (canvas.width - dw) / 2; oy = 0; }
        ctx.drawImage(bgImg, ox, oy, dw, dh);
        if (strokesImg.width > 0) ctx.drawImage(strokesImg, 0, 0);
        result = canvas.toDataURL("image/png");
      } else {
        result = strokesDataUrl;
      }
      lastCaptureRef.current = result;
      onSave(result);
      return result;
    } catch (err) { console.error("Failed to capture canvas:", err); return canvasBg || null; }
  };

  // Debounced so a burst of quick strokes doesn't run the compositing work
  // once per stroke; 400ms is well inside the time it takes to switch notes.
  const strokeTimerRef = useRef(null);
  const handleStroke = () => {
    if (strokeTimerRef.current) clearTimeout(strokeTimerRef.current);
    strokeTimerRef.current = setTimeout(() => { capture(); }, 400);
  };

  // Flush a pending debounce, then capture. Callers that are about to leave the
  // canvas use this so the last stroke can't be lost to the timer.
  const flush = async () => {
    if (strokeTimerRef.current) {
      clearTimeout(strokeTimerRef.current);
      strokeTimerRef.current = null;
    }
    return capture();
  };

  useEffect(() => () => {
    if (strokeTimerRef.current) clearTimeout(strokeTimerRef.current);
  }, []);

  // `capture` alone can't answer "did the user actually draw?" — with no
  // existing image it happily exports a blank canvas. Callers deciding whether
  // there is anything worth saving need this instead.
  const hasStrokes = async () => {
    if (!canvasRef.current) return false;
    try {
      const paths = await canvasRef.current.exportPaths();
      return Array.isArray(paths) && paths.length > 0;
    } catch {
      return false;
    }
  };

  useImperativeHandle(ref, () => ({ capture, hasStrokes, flush }));

  return (
    <div>
      {/* Toolbar */}
      <div className="handwriting-modal-header d-flex align-items-center gap-2 flex-wrap p-2 mb-1" style={{ backgroundColor: "#CFE2FF" }}>
        <div className="d-flex align-items-center gap-1">
          {colorOptions.map((color) => (
            <button key={color} type="button"
              className={`color-btn ${strokeColor === color && !isEraser && !isHighlighter ? "active" : ""}`}
              style={{ backgroundColor: color }}
              onClick={() => { setStrokeColor(color); setIsEraser(false); setIsHighlighter(false); setStrokeWidth(3); canvasRef.current?.eraseMode(false); }}
            />
          ))}
        </div>
        <div className="d-flex align-items-center gap-1">
          {strokeOptions.map((w) => (
            <button key={w} type="button"
              className={`stroke-btn ${strokeWidth === w && !isHighlighter ? "active" : ""}`}
              onClick={() => { setStrokeWidth(w); setIsHighlighter(false); }}
            >
              <span style={{ width: w * 2, height: w * 2 }} className="stroke-preview" />
            </button>
          ))}
        </div>
        <div className="d-flex align-items-center gap-1">
          <OverlayTrigger placement="top" overlay={<Tooltip>Highlighter</Tooltip>}>
            <Button variant={isHighlighter ? "warning" : "outline-primary"} size="sm" onClick={toggleHighlighter}><i className="fa-solid fa-highlighter" /></Button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Eraser</Tooltip>}>
            <Button variant={isEraser ? "primary" : "outline-primary"} size="sm" onClick={toggleEraser}><i className="fa-solid fa-eraser" /></Button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Undo</Tooltip>}>
            <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.undo()}><i className="fa-solid fa-rotate-left" /></Button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Redo</Tooltip>}>
            <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.redo()}><i className="fa-solid fa-rotate-right" /></Button>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Clear</Tooltip>}>
            <Button variant="outline-primary" size="sm" onClick={() => canvasRef.current?.clearCanvas()}><i className="fa-solid fa-trash" /></Button>
          </OverlayTrigger>
        </div>
        <div className="ms-auto d-flex align-items-center gap-2">
          <OverlayTrigger placement="top" overlay={<Tooltip>{isFullScreen ? "Collapse" : "Expand"}</Tooltip>}>
            <Button variant="link" className="text-primary text-decoration-none p-0 px-2" onClick={onExpand} aria-label={isFullScreen ? "Collapse" : "Expand"}>
              <i className={`fa-solid ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
            </Button>
          </OverlayTrigger>
        </div>
      </div>
      {/* Canvas */}
      <div className="handwriting-canvas-container" style={{ width: "100%", height: "1200px", overflow: "hidden" }}>
        <ReactSketchCanvas
          ref={canvasRef}
          strokeWidth={isHighlighter ? 20 : strokeWidth}
          strokeColor={isEraser ? "transparent" : (isHighlighter ? "rgba(255,255,0,0.4)" : strokeColor)}
          canvasColor="transparent"
          backgroundImage={canvasBg}
          preserveBackgroundImageAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "100%" }}
          allowOnlyPointerType="all"
          withTimestamp={true}
          onStroke={handleStroke}
        />
      </div>
    </div>
  );
});

const HandwritingModal = ({ show, onHide, onSave, existingImage }) => {
  const canvasRef = useRef(null);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [isEraser, setIsEraser] = useState(false);
  const [isHighlighter, setIsHighlighter] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);

  // Load existing image as background when modal opens
  useEffect(() => {
    if (show && existingImage) {
      setBackgroundImage(existingImage);
    } else if (!show) {
      // Reset when modal closes
      setBackgroundImage(null);
    }
  }, [show, existingImage]);

  const handleSave = async () => {
    if (canvasRef.current) {
      try {
        // Check if user drew any new strokes
        const paths = await canvasRef.current.exportPaths();
        const hasNewStrokes = paths && paths.length > 0;

        // If there's a background but no new strokes, keep the original unchanged
        if (backgroundImage && !hasNewStrokes) {
          onSave(backgroundImage);
          onHide();
          return;
        }

        // Export the strokes layer at native canvas dimensions
        const strokesDataUrl = await canvasRef.current.exportImage("png");

        // If there's a background image, merge it with the strokes
        if (backgroundImage && hasNewStrokes) {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Load strokes image first to get canvas dimensions
          const strokesImg = new Image();
          strokesImg.crossOrigin = "anonymous";

          await new Promise((resolve, reject) => {
            strokesImg.onload = resolve;
            strokesImg.onerror = reject;
            strokesImg.src = strokesDataUrl;
          });

          // Load background image
          const bgImg = new Image();
          bgImg.crossOrigin = "anonymous";

          await new Promise((resolve, reject) => {
            bgImg.onload = resolve;
            bgImg.onerror = reject;
            bgImg.src = backgroundImage;
          });

          // Use strokes canvas dimensions as the base
          canvas.width = strokesImg.width || 800;
          canvas.height = strokesImg.height || 600;

          // Calculate aspect-ratio-preserving dimensions for background
          const bgAspect = bgImg.width / bgImg.height;
          const canvasAspect = canvas.width / canvas.height;

          let drawWidth, drawHeight, offsetX, offsetY;

          if (bgAspect > canvasAspect) {
            // Background is wider - fit to width
            drawWidth = canvas.width;
            drawHeight = canvas.width / bgAspect;
            offsetX = 0;
            offsetY = (canvas.height - drawHeight) / 2;
          } else {
            // Background is taller - fit to height
            drawHeight = canvas.height;
            drawWidth = canvas.height * bgAspect;
            offsetX = (canvas.width - drawWidth) / 2;
            offsetY = 0;
          }

          // Draw background centered and aspect-ratio preserved (keep transparent)
          ctx.drawImage(bgImg, offsetX, offsetY, drawWidth, drawHeight);

          // Draw strokes on top at their native size (no scaling needed)
          if (strokesImg.width > 0 && strokesImg.height > 0) {
            ctx.drawImage(strokesImg, 0, 0);
          }

          // Export merged image
          const mergedDataUrl = canvas.toDataURL("image/png");
          onSave(mergedDataUrl);
        } else {
          // No background, just use the strokes
          onSave(strokesDataUrl);
        }

        onHide();
      } catch (err) {
        console.error("Failed to export canvas:", err);
      }
    }
  };

  const handleUndo = () => canvasRef.current?.undo();
  const handleRedo = () => canvasRef.current?.redo();
  const handleClear = () => canvasRef.current?.clearCanvas();

  const toggleEraser = () => {
    const next = !isEraser;
    setIsEraser(next);
    setIsHighlighter(false);
    if (canvasRef.current) {
      canvasRef.current.eraseMode(next);
    }
  };

  const toggleHighlighter = () => {
    const next = !isHighlighter;
    setIsHighlighter(next);
    setIsEraser(false);
    if (canvasRef.current) {
      canvasRef.current.eraseMode(false);
    }
    if (next) {
      setStrokeColor("rgba(255, 255, 0, 0.4)");
      setStrokeWidth(20);
    } else {
      setStrokeColor("#000000");
      setStrokeWidth(3);
    }
  };

  const colorOptions = ["#000000", "#0f3490", "#dc3545", "#198754", "#6c757d"];
  const strokeOptions = [2, 3, 5, 8];

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      className="handwriting-modal"
      backdrop="static"
    >
      <Modal.Header
        className="handwriting-modal-header py-2"
        style={{ backgroundColor: "#CFE2FF" }}
      >
        <div className="d-flex align-items-center gap-3 flex-wrap w-100">
          {/* Color picker */}
          <div className="d-flex align-items-center gap-1">
            {colorOptions.map((color) => (
              <button
                key={color}
                type="button"
                className={`color-btn ${strokeColor === color && !isEraser && !isHighlighter ? "active" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => {
                  setStrokeColor(color);
                  setIsEraser(false);
                  setIsHighlighter(false);
                  setStrokeWidth(3);
                  canvasRef.current?.eraseMode(false);
                }}
                aria-label={`Color ${color}`}
              />
            ))}
          </div>

          {/* Stroke width */}
          <div className="d-flex align-items-center gap-1">
            {strokeOptions.map((width) => (
              <button
                key={width}
                type="button"
                className={`stroke-btn ${strokeWidth === width && !isHighlighter ? "active" : ""}`}
                onClick={() => {
                  setStrokeWidth(width);
                  setIsHighlighter(false);
                }}
                aria-label={`Stroke width ${width}`}
              >
                <span
                  style={{ width: width * 2, height: width * 2 }}
                  className="stroke-preview"
                />
              </button>
            ))}
          </div>

          {/* Tools */}
          <div className="d-flex align-items-center gap-2">
            <OverlayTrigger placement="top" overlay={<Tooltip>Highlighter</Tooltip>}>
              <Button
                variant={isHighlighter ? "warning" : "outline-primary"}
                size="sm"
                onClick={toggleHighlighter}
              >
                <i className="fa-solid fa-highlighter" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Eraser</Tooltip>}>
              <Button
                variant={isEraser ? "primary" : "outline-primary"}
                size="sm"
                onClick={toggleEraser}
              >
                <i className="fa-solid fa-eraser" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Undo</Tooltip>}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleUndo}
              >
                <i className="fa-solid fa-rotate-left" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Redo</Tooltip>}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleRedo}
              >
                <i className="fa-solid fa-rotate-right" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Clear all</Tooltip>}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleClear}
              >
                <i className="fa-solid fa-trash" />
              </Button>
            </OverlayTrigger>
          </div>

          {/* Actions */}
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button variant="outline-primary" size="sm" onClick={onHide}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave}>
              Save Drawing
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-0 handwriting-canvas-container" style={{ overflowY: "auto" }}>
        <div style={{ width: "100%", height: "200vh", position: "relative" }}>
          <ReactSketchCanvas
            ref={canvasRef}
            strokeWidth={strokeWidth}
            strokeColor={isEraser ? "transparent" : strokeColor}
            canvasColor="transparent"
            backgroundImage={backgroundImage || ""}
            preserveBackgroundImageAspectRatio="xMidYMid meet"
            style={{
              width: "100%",
              height: "100%",
            }}
            allowOnlyPointerType="all"
            withTimestamp={true}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

// Notepad Section Component
const NotepadSection = ({
  notepadContent,
  setNotepadContent,
  onOpenNotepadRecorder,
  isMobile,
  onSubmit,
  submitLoading,
  onExpand,
  isFullScreen,
}) => {
  const quillRef = useRef(null);

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
    ],
  };

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "list",
    "bullet",
    "align",
    "link",
  ];

  return (
    <div className="mt-1">
      <Row className="align-items-center mb-1">
        <Col md={12}>
          <div className="d-flex align-items-center">
            <p className="text-muted mb-0 mt-1">
              Use this space for quick notes during the session.
            </p>

            {/* Icons aligned to the far right */}
            <div className="d-flex align-items-center ms-auto gap-2">
              <OverlayTrigger placement="top" overlay={<Tooltip>Speak to add to notepad</Tooltip>}>
                <Button
                  variant="link"
                  size="sm"
                  className="p-1 border-0 text-primary text-decoration-none"
                  aria-label="Speak to add to notepad"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenNotepadRecorder(); }}
                >
                  <i className="fa-light fa-microphone fs-6" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>{isFullScreen ? "Collapse" : "Expand"}</Tooltip>}>
                <Button variant="link" className="text-primary text-decoration-none p-0 px-2" onClick={onExpand} aria-label={isFullScreen ? "Collapse" : "Expand"}>
                  <i className={`fa-solid ${isFullScreen ? "fa-arrows-minimize" : "fa-arrows-maximize"} fs-5`} />
                </Button>
              </OverlayTrigger>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <div className="bg-white rounded shadow-sm overflow-hidden notepad-container">
            <ReactQuill
              ref={quillRef}
              value={notepadContent}
              onChange={setNotepadContent}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Start typing your notes here..."
              style={{
                minHeight: "60vh",
                height: "60vh",
              }}
            />
          </div>
        </Col>
      </Row>

    </div>
  );
};

// Media Section Component
const MediaSection = ({ patientId }) => (
  <div className="mt-3">
    <Row className="align-items-center mb-3">
      <Col md={12}>
        <h5 className="text-primary fw-bold mb-2">Media & Files</h5>
        <p className="text-muted mb-0">
          Attach or view media files related to this appointment.
        </p>
      </Col>
    </Row>

    <Row>
      <Col md={12}>
        <div className="bg-white rounded p-3 shadow-sm">
          <PatientFiles patientId={patientId} />
        </div>
      </Col>
    </Row>
  </div>
);

// Submit Button Component
const SubmitButton = ({ onClick, loading = false }) => (
  <div className="d-flex justify-content-center mt-4">
    <Button
      variant="primary"
      size="lg"
      onClick={onClick}
      disabled={loading}
      className="d-flex align-items-center justify-content-center gap-2 px-4 py-2 rounded-0"
    >
      <i className="fa-regular fa-floppy-disk"></i>
      {loading ? "Processing..." : "Submit All Sections"}
    </Button>
  </div>
);

// Main Component
export const FollowUpSession = forwardRef(
  (
    {
      appointmentId,
      patientId,
      serviceName,
      resourceId,
      appointmentStart,
      appointmentStatus,
      practiceId,
      onUpdate,
      activeSection,
      adHoc = false,
      syncToProps,
      // One note = one kind. When supplied, only this kind's editor renders and
      // the value is persisted on save.
      sessionType,
      // Ties this note to the ad-hoc sitting it was written in.
      adHocGroupId,
      // The appointment's note list, rendered where the section nav used to be.
      noteRail,
  // SyncTo moved to the toolbar above the box on the appointment view.
  hideInlineSyncTo = false,
    },
    ref,
  ) => {
    // State management
    const { toast, showToast, hideToast } = useToast();
    const { confirm, confirmProps } = useConfirm();
    const [successAlert, setSuccessAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Opt-in: mark the linked appointment Complete on save. Default driven by
    // the user's preferences.autoCompleteAppointments flag (off unless set).
    // Hidden entirely for ad-hoc notes and for already-completed appointments.
    const [completeAppointment, setCompleteAppointment] = useState(
      !!getUserPreferences().autoCompleteAppointments,
    );
    const [showRotateMessage, setShowRotateMessage] = useState(false);
    /**
     * The submit the rotate overlay interrupted, so it can be finished once the
     * device turns.
     *
     * Without this the overlay simply ate the submit: handleSubmit returned
     * early, rotating only hid the overlay, and the note was never saved. From
     * the user's side they pressed Save, were told to rotate, rotated, and
     * nothing happened, with no indication that the note was still unsaved.
     */
    const pendingSubmitRef = useRef(null);
    const resumeSubmitRef = useRef(null);
    const [mediaImages, setMediaImages] = useState([]);

    // Data state
    const [appointment, setAppointment] = useState(null);
    const [account, setAccount] = useState({});
    const [templates, setTemplates] = useState([]);
    const [dropdownOptions, setDropdownOptions] = useState([]);

    // Section-specific state
    const [sessionTemplate, setSessionTemplate] = useState("");
    const [notes, setNotes] = useState([]);
    const [notepadContent, setNotepadContent] = useState("");
    const [handwritingImage, setHandwritingImage] = useState(null);
    const [showHandwritingModal, setShowHandwritingModal] = useState(false);
    const [localSection, setLocalSection] = useState(activeSection || "apptTemplate");
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isNarrow, setIsNarrow] = useState(window.innerWidth <= 1024);
    const [isLandscape, setIsLandscape] = useState(
      window.matchMedia("(orientation: landscape)").matches,
    );
    const annotatorRefs = useRef([]);
    const inlineWriteRef = useRef(null);

    // NoteRenderer fill mode state
    const [capturedValues, setCapturedValues] = useState({});
    const [templatePages, setTemplatePages] = useState([]);
    const [templateVersion, setTemplateVersion] = useState(0);
    const [serverTimestamp, setServerTimestamp] = useState(null);
    const [lastSaved, setLastSaved] = useState(null);
    const [savedSessionId, setSavedSessionId] = useState(null);
    // State won't have flushed by the time an imperative saveNow() returns, so
    // mirror the id in a ref for callers that need it immediately.
    const savedSessionIdRef = useRef(null);
    const hasUserEditedRef = useRef(false);
    const autosaveTimerRef = useRef(null);
    const [voiceTargetId, setVoiceTargetId] = useState(null);

    // Detect mobile device for handwriting button visibility
    useEffect(() => {
      const checkMobile = () => {
        const mobile =
          window.innerWidth <= 1024 ||
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
          );
        setIsMobile(mobile);
        setIsNarrow(window.innerWidth <= 1024);
        setIsLandscape(window.matchMedia("(orientation: landscape)").matches);
      };
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const navigate = useNavigate();

    // A note's kind decides which editor body shows. Kept separate from the
    // activeSection sync below so an explicit kind always wins — a "media" note
    // must not open on the template tab because the page's section state
    // happened to say apptTemplate.
    const SECTION_FOR_KIND = {
      template: "apptTemplate",
      note: "apptNotepad",
      write: "write",
      media: "apptMedia",
    };

    // Sync external activeSection prop → localSection
    useEffect(() => {
      if (sessionType && SECTION_FOR_KIND[sessionType]) {
        setLocalSection(SECTION_FOR_KIND[sessionType]);
        return;
      }
      if (activeSection) setLocalSection(activeSection);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection, sessionType]);

    // Imperative handle for external completion
    // Is there anything worth persisting? Guards the implicit save below so
    // clicking Add on an untouched note doesn't leave an empty one behind.
    const hasUnsavedContent = () => {
      const plain = (notepadContent || "").replace(/<[^>]*>/g, "").trim();
      return (
        !!sessionTemplate ||
        plain.length > 0 ||
        !!handwritingImage ||
        (Array.isArray(mediaImages) && mediaImages.length > 0) ||
        (capturedValues && Object.keys(capturedValues).length > 0)
      );
    };

    useImperativeHandle(ref, () => ({
      async handleExternalCompletion() {
        if (!sessionTemplate && localSection === "apptTemplate") {
          return false;
        }
        await handleSubmit();
        return true;
      },
      /**
       * Persist without navigating — used when the user starts a SECOND note
       * (Add) or a SyncTo recording while this one is still unsaved. Returns
       * the note's id, or null when there was nothing to save.
       */
      async saveNow() {
        // An in-progress drawing lives only on the canvas until it's exported,
        // so commit it BEFORE deciding whether there's anything to save —
        // otherwise leaving a half-finished sketch silently discarded it.
        let captured = null;
        if (localSection === "write" && inlineWriteRef.current) {
          if (await inlineWriteRef.current.hasStrokes()) {
            captured = await inlineWriteRef.current.flush();
            if (captured) setHandwritingImage(captured);
          }
        }
        // This note has already been created once (an earlier implicit save, a
        // SyncTo recording), and handleSubmit only ever creates — running it
        // again would leave a duplicate note behind. Write the drawing onto the
        // note that exists instead; strokes made after that first save used to
        // be dropped here.
        if (savedSessionIdRef.current) {
          if (captured && captured !== handwritingImage) {
            try {
              await updateSessionById(savedSessionIdRef.current, {
                handwritingImage: captured,
              });
            } catch (err) {
              console.error("Could not save the drawing onto this note:", err);
            }
          }
          return savedSessionIdRef.current;
        }
        if (!captured && !hasUnsavedContent()) return null;
        await handleSubmit({ finish: false });
        return savedSessionIdRef.current;
      },
      hasUnsavedContent,
    }));

    // Initialize data
    useEffect(() => {
      const initializeData = async () => {
        const token = sessionStorage.getItem("user");
        if (!token) return;

        try {
          const decoded = jwtDecode(token);
          const userPractices = decoded.practice || [];

          const promises = [
            fetchPracticeTemplates(decoded._id),
            getAccountByPatientId(patientId),
          ];

          // Only fetch appointment data if not ad-hoc
          if (!adHoc && appointmentId) {
            promises.push(getAppointmentById(appointmentId));
          }

          const [templatesData, accountData, appointmentData] =
            await Promise.all(promises);

          setAccount(accountData);

          // Build template buckets
          const filteredTemplates = (templatesData || []).filter((t) =>
            t.templatePracticeId?.some((pid) => userPractices.includes(pid)),
          );

          // Dropdown source: hide inactive templates and sort alphabetically.
          // `filteredTemplates` is kept intact so existing selections still resolve.
          const selectableTemplates = filteredTemplates
            .filter((t) => t.templateStatus !== "Inactive")
            .sort((a, b) =>
              (a.templateTitle || "").localeCompare(b.templateTitle || "", undefined, { sensitivity: "base" })
            );

          const initialTemplates = selectableTemplates.filter(
            (t) => t.templateInitial === "initial",
          );
          const followUpTemplates = selectableTemplates.filter(
            (t) => t.templateInitial === "followup",
          );

          setDropdownOptions([
            { label: "Initial Assessment", options: initialTemplates },
            { label: "Follow Up", options: followUpTemplates },
          ]);
          setTemplates(filteredTemplates);

          // Only set appointment state in appointment mode
          if (!adHoc && appointmentData) {
            setAppointment(appointmentData);
          }
        } catch (error) {
          console.error("Error initializing data:", error);
        }
      };

      initializeData();
    }, [appointmentId, patientId, adHoc]);

    // inside FollowUpSession component:
    const vr = useVoiceRecorder(); // use the new hook

    // Which target opened the recorder
    // { type: 'template', index: number } | { type: 'notepad' }
    const [recTarget, setRecTarget] = useState(null);
    const [recOpen, setRecOpen] = useState(false);

    const openRecorderForTemplate = (index) => {
      setRecTarget({ type: "template", index });
      setRecOpen(true);
    };
    const openRecorderForNotepad = () => {
      setRecTarget({ type: "notepad" });
      setRecOpen(true);
    };
    const closeRecorder = () => {
      // ensure we stop if still listening
      if (vr.listening) vr.stop();
      setRecOpen(false);
      setRecTarget(null);
      setVoiceTargetId(null); // clears NoteRenderer field target too
    };

    const applyTranscriptToTarget = (text) => {
      const cleaned = (text || "").trim();
      if (!cleaned) return;

      if (voiceTargetId) {
        // NoteRenderer field voice path — applies transcript to capturedValues by element ID
        hasUserEditedRef.current = true;
        setCapturedValues(prev => ({
          ...prev,
          [voiceTargetId]: prev[voiceTargetId]
            ? prev[voiceTargetId] + ' ' + cleaned
            : cleaned,
        }));
        setVoiceTargetId(null);
      } else if (recTarget?.type === "notepad") {
        // Notepad voice path — append to notepad
        setNotepadContent((prev) => {
          const base = (prev || "").toString();
          return base ? `${base} ${cleaned}` : cleaned;
        });
      }
    };

    const handleTemplateChange = async (selectedId) => {
      // If the session has been saved, changing the template will overwrite
      // it. Gate the change on a confirm dialog — the user must explicitly
      // agree (or be steered toward "Add Note" for a fresh session).
      if (savedSessionId && selectedId !== sessionTemplate) {
        const ok = await confirm({
          title: "Overwrite saved session?",
          message:
            "Changing the template will overwrite this saved session.\n\n" +
            "If you want to record a new session, cancel and use \"Add Note\" instead.",
          confirmLabel: "Overwrite",
          cancelLabel: "Cancel",
          confirmVariant: "danger",
        });
        if (!ok) return;
      }
      setSessionTemplate(selectedId);
      const sel = templates.find((t) => t._id === selectedId);
      const pages = sel
        ? (Array.isArray(sel.pages) && sel.pages.length > 0 ? sel.pages : notesToPages(sel.notes))
        : [];
      setTemplatePages(pages);
      setTemplateVersion(sel?.currentVersion || 0);
      // Build initial values from defaultValue on each element
      const init = {};
      for (const page of pages) {
        for (const item of (page.items || [])) {
          if (item.kind === 'group') {
            for (const child of (item.children || [])) {
              if (child.defaultValue !== undefined) init[child.id] = child.defaultValue;
            }
          } else if (item.defaultValue !== undefined) {
            init[item.id] = item.defaultValue;
          }
        }
      }
      setCapturedValues(init);
      hasUserEditedRef.current = false;
    };

    // Autosave — fires 2 seconds after capturedValues changes, gated by savedSessionId and user edit flag
    useEffect(() => {
      if (!savedSessionId || !sessionTemplate) return;
      if (!hasUserEditedRef.current) return;
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = setTimeout(async () => {
        try {
          // Resolve practiceId for autosave (same logic as handleSubmit)
          let resolvedPracticeId =
            (practiceId != null && String(practiceId)) || null;
          if (!resolvedPracticeId) {
            try {
              const token = sessionStorage.getItem("user");
              if (token) {
                const { jwtDecode: decode } = await import("jwt-decode");
                const decoded = decode(token);
                if (Array.isArray(decoded.practice) && decoded.practice.length > 0) {
                  resolvedPracticeId = String(decoded.practice[0]);
                }
              }
            } catch {}
          }
          const resp = await updateSessionById(savedSessionId, {
            sessionsPatientId: String(patientId || "").trim(),
            sessionsPracticeId: resolvedPracticeId,
            capturedValues,
            sessionsTemplateVersion: templateVersion,
            clientTimestamp: serverTimestamp,
            templatePages,
          });
          setServerTimestamp(resp.updatedAt || null);
          setLastSaved(new Date());
        } catch (err) {
          if (err.response?.status === 409) {
            // Session was updated elsewhere — skip this autosave silently
          }
        }
      }, 2000);
      return () => clearTimeout(autosaveTimerRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [capturedValues]);

    // keep refs to each ImageAnnotator so we can merge edits on demand

    useEffect(() => {
      // keep refs array in sync with notes length
      annotatorRefs.current = annotatorRefs.current.slice(0, notes.length);
    }, [notes.length]);

    // commit ALL image edits (used before leaving the tab and before saving)
    const commitAllImages = async () => {
      const copy = [...notes];
      let changed = false;
      for (let idx = 0; idx < copy.length; idx++) {
        const note = copy[idx];
        if (note?.sessionsFieldType !== "image") continue;
        const ref = annotatorRefs.current[idx];
        if (!ref || typeof ref.mergeImage !== "function") continue;
        try {
          const hasStrokes = typeof ref.hasStrokes === "function" && await ref.hasStrokes();
          if (!hasStrokes) continue;
          const merged = await ref.mergeImage();
          if (merged && typeof merged === "string") {
            copy[idx] = { ...note, sessionsImageURL: merged };
            changed = true;
          }
        } catch (e) {
          console.warn("mergeImage failed for index", idx, e);
        }
      }
      if (changed) setNotes(copy);
      return copy;
    };

    useEffect(() => {
      if (activeSection !== "apptTemplate") {
        commitAllImages();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    /*
     * Finish the submit the overlay interrupted.
     *
     * Reads and clears the pending args in one go, so a rotation that fires
     * resize, orientationchange and a media query change all at once still
     * submits exactly once. skipRotateCheck is what stops "Continue anyway"
     * from bouncing straight back into the overlay it just closed.
     *
     * Held in a ref and refreshed every render because handleSubmit is rebuilt
     * on each one; a listener that captured an old copy would submit stale
     * note content.
     */
    useEffect(() => {
      resumeSubmitRef.current = () => {
        const pending = pendingSubmitRef.current;
        pendingSubmitRef.current = null;
        setShowRotateMessage(false);
        if (pending) {
          void handleSubmit({ ...pending, skipRotateCheck: true });
        }
      };
    });

    // Rotating the device finishes what the overlay interrupted.
    useEffect(() => {
      if (!showRotateMessage) return undefined;

      const mql = window.matchMedia("(orientation: landscape)");

      const check = () => {
        if (mql.matches || window.innerWidth > window.innerHeight) {
          resumeSubmitRef.current?.();
        }
      };

      /*
       * iOS reports the pre-rotation dimensions on the first resize of a
       * rotation, so a check that runs only on the event can miss the turn
       * entirely and leave the overlay up. Re-check once the viewport settles.
       */
      const onEvent = () => {
        check();
        window.setTimeout(check, 250);
      };

      window.addEventListener("resize", onEvent);
      window.addEventListener("orientationchange", onEvent);
      mql.addEventListener?.("change", onEvent);

      return () => {
        window.removeEventListener("resize", onEvent);
        window.removeEventListener("orientationchange", onEvent);
        mql.removeEventListener?.("change", onEvent);
      };
    }, [showRotateMessage]);


    // Updated handleSubmit method for FollowUpSession component

    const handleSubmit = async ({ finish = true, skipRotateCheck = false } = {}) => {
      /*
       * Ask for landscape before FINISHING, because finishing pushes the user
       * into the invoice, which needs the width.
       *
       * `finish` matters. saveNow() calls this with finish:false to persist
       * quietly when a second note or a SyncTo recording starts; that path
       * navigates nowhere, so blocking it only threw an overlay at someone who
       * never pressed Save and dropped the note on the floor, with saveNow()
       * reporting null to a caller that had no reason to doubt it.
       */
      const isPortraitNow = window.innerHeight > window.innerWidth;
      if (
        finish &&
        !skipRotateCheck &&
        !adHoc &&
        window.innerWidth < 768 &&
        isPortraitNow
      ) {
        pendingSubmitRef.current = { finish };
        setShowRotateMessage(true);
        return;
      }

      // 🔹 Resolve practice and patient IDs robustly
      const resolvedPatientId = String(patientId || "").trim();
      // Try in this order: prop -> account.practiceNumber-ish -> first practice from JWT
      let resolvedPracticeId =
        (practiceId != null && String(practiceId)) ||
        (account?.accountPracticeNumber &&
          String(account.accountPracticeNumber)) ||
        null;

      if (!resolvedPracticeId) {
        try {
          const token = sessionStorage.getItem("user");
          if (token) {
            const decoded = jwtDecode(token);
            // decoded.practice could be: array of practice IDs or numbers
            if (
              Array.isArray(decoded.practice) &&
              decoded.practice.length > 0
            ) {
              resolvedPracticeId = String(decoded.practice[0]);
            }
          }
        } catch {}
      }

      if (!resolvedPatientId || !resolvedPracticeId) {
        setErrorMessage(
          "Missing Patient or Practice. Please wait a moment and try again.",
        );
        return;
      }

      setErrorMessage(false); // a fresh attempt should not sit under an old failure
      clearTimeout(autosaveTimerRef.current); // cancel any pending autosave
      setIsSaving(true);
      try {
        // Auto-capture handwriting canvas before saving — only when visible (display:none breaks exportImage)
        let currentHandwriting = handwritingImage;
        // Only meaningful while the canvas is on screen — exportImage fails on
        // display:none. When it is hidden, `handwritingImage` is already
        // current: onStroke commits every stroke as it is drawn.
        if (localSection === "write" && inlineWriteRef.current) {
          const captured = await inlineWriteRef.current.flush();
          if (captured) { currentHandwriting = captured; setHandwritingImage(captured); }
        }

        // A) Merge inline annotator edits first
        const mergedNotes = await commitAllImages();

        // B) Upload template/annotator images (convert data URLs only)
        const processedNotes = [];
        for (let i = 0; i < (mergedNotes || []).length; i++) {
          const n = mergedNotes[i];
          if (n.sessionsFieldType !== "image") {
            processedNotes.push(n);
            continue;
          }

          const url =
            typeof n.sessionsImageURL === "string" ? n.sessionsImageURL : "";
          const isDataUrl = url.startsWith("data:");

          if (url && isDataUrl) {
            const blob = await fetch(url).then((r) => r.blob());
            const formData = new FormData();
            formData.append("image", blob);
            formData.append("x-image-caption", "Session Image Upload");
            formData.append("patientId", resolvedPatientId);
            if (!adHoc && appointmentId)
              formData.append("appointmentId", appointmentId);
            formData.append("type", "session");
            const uploadResp = await uploadImage(formData);
            processedNotes.push({
              ...n,
              sessionsImageURL: uploadResp.imageUrl,
            });
          } else {
            processedNotes.push(n);
          }
        }

        // C) Upload media photos (convert data URLs only)
        const uploadedMediaFiles = [];
        for (let idx = 0; idx < (mediaImages || []).length; idx++) {
          const m = mediaImages[idx];
          if (typeof m !== "string") continue;

          if (m.startsWith("http://") || m.startsWith("https://")) {
            uploadedMediaFiles.push({
              fileName: `media-photo-${idx + 1}`,
              fileUrl: m,
              fileType: "image",
              uploadedAt: new Date().toISOString(),
              description: `Captured photo ${idx + 1}`,
            });
            continue;
          }

          if (m.startsWith("data:")) {
            const blob = await fetch(m).then((r) => r.blob());
            const formData = new FormData();
            formData.append("image", blob);
            formData.append("x-image-caption", `Media Photo ${idx + 1}`);
            formData.append("patientId", resolvedPatientId);
            if (!adHoc && appointmentId)
              formData.append("appointmentId", appointmentId);
            formData.append("type", "session");
            const uploadResp = await uploadImage(formData);
            uploadedMediaFiles.push({
              fileName: `media-photo-${idx + 1}`,
              fileUrl: uploadResp.imageUrl,
              fileType: "image",
              uploadedAt: new Date().toISOString(),
              description: `Captured photo ${idx + 1}`,
            });
          }
        }

        // D) De-dup media by URL
        const seen = new Set();
        const dedupedMediaFiles = uploadedMediaFiles.filter((f) => {
          if (seen.has(f.fileUrl)) return false;
          seen.add(f.fileUrl);
          return true;
        });

        // E) Build payload (omit appointment fields in ad-hoc mode)
        const sessionsInitial = sessionTemplate
          ? templates.find((t) => t._id === sessionTemplate)
              ?.templateInitial === "initial"
          : false;
        const nowIso = new Date().toISOString();

        const payload = {
          sessionsPatientId: resolvedPatientId,
          sessionsPracticeId: resolvedPracticeId,
          ...(adHoc
            ? {
                sessionsAdHoc: true,
                // ⬇️ ensure ad-hoc notes have a timestamp
                sessionsAppointmentStart: appointmentStart || nowIso,
              }
            : {
                sessionsAppointmentId: appointmentId,
                sessionsServiceName: serviceName,
                sessionsResourceId: resourceId,
                sessionsAppointmentStart: appointmentStart || nowIso, // extra guard for appt too
              }),
          ...(sessionType ? { sessionType } : {}),
          ...(adHoc && adHocGroupId ? { sessionsAdHocGroupId: adHocGroupId } : {}),
          ...(sessionTemplate
            ? { sessionsTemplateId: sessionTemplate, sessionsInitial }
            : {}),
          ...(processedNotes.length
            ? {
                sections: processedNotes.map((note) => ({
                  sessionsSection: note.sessionsSection,
                  sessionsFieldType: note.sessionsFieldType,
                  sessionsSectionNotes:
                    note.sessionsFieldType === "multiselect"
                      ? {
                          availableOptions:
                            note.sessionsSectionNotes?.availableOptions || [],
                          selectedOptions:
                            note.sessionsSectionNotes?.selectedOptions || [],
                        }
                      : typeof note.sessionsSectionNotes === "string"
                        ? note.sessionsSectionNotes
                        : "",
                  sessionsImageURL:
                    note.sessionsFieldType === "image" &&
                    typeof note.sessionsImageURL === "string"
                      ? note.sessionsImageURL
                      : "",
                })),
              }
            : {}),
          ...(notepadContent?.trim()
            ? { notepadContent, notepadContentType: "html" }
            : {}),
          ...(currentHandwriting ? { handwritingImage: currentHandwriting } : {}),
          ...(dedupedMediaFiles.length
            ? { mediaFiles: dedupedMediaFiles }
            : {}),
          capturedValues,
          sessionsTemplateVersion: templateVersion,
          clientTimestamp: serverTimestamp,
          templatePages,
        };

        // F) Persist
        const createResp = await createSession(payload); // <- works for both appointment + ad-hoc
        // Store the created sessionId for autosave on subsequent edits
        const newSessionId = String(createResp?.sessionId || createResp?._id || '');
        if (newSessionId) {
          setSavedSessionId(newSessionId);
          savedSessionIdRef.current = newSessionId;
        }
        setServerTimestamp(createResp?.updatedAt || null);
        setLastSaved(new Date());

        // An implicit save — the user clicked Add, picked another note, or
        // started a recording. Persist and stay exactly where they are; the
        // completion / invoice / calendar flow belongs to the Save button.
        if (!finish) {
          showToast("success", "Note saved.");
          onUpdate?.(newSessionId, { stayPut: true });
          return;
        }

        const isUnregistered = String(resolvedPatientId).trim() === "1";
        if (isUnregistered) {
          setSuccessAlert(true);
          navigate(getBookingsReturnPath());
          return;
        }

        if (adHoc) {
          // Ad-hoc: no status updates, no invoice navigation
          setSuccessAlert(true);
          onUpdate?.(newSessionId);
          return;
        }

        // Opt-in appointment completion. Only when the checkbox is ticked,
        // this isn't ad-hoc, and the appointment isn't already Completed.
        // "New Patient" → "Completed" is blocked at the API layer; skip the
        // call here so we don't fire a request we know will be rejected.
        if (
          completeAppointment &&
          appointmentId &&
          appointmentStatus !== "Completed" &&
          appointmentStatus !== "New Patient"
        ) {
          try {
            await updateAppointmentStatusByType(appointmentId, "Completed");
          } catch (statusErr) {
            console.error("Failed to mark appointment complete:", statusErr);
          }
        }

        // Finishing up — hand off the way the flow always did: straight into
        // the invoice. Already-invoiced appointments have nothing to raise, so
        // those return to the calendar instead.
        if (appointment?.isInvoiced || !account?._id) {
          navigate(getBookingsReturnPath());
          return;
        }
        const appt = appointment;
        navigate(`/invoices/1/${account._id}`, {
          state: {
            // service to pre-select and expand
            servicesId: appt?.serviceId,

            // appointment linking
            appointmentId: appt?._id || appt?.id,
            appointmentStart: appt?.start || appt?.startTime || null, // ISO if available
            appointmentEnd: appt?.end || appt?.endTime || null, // ISO if available

            // who/what
            patientId: appt?.patientId,
            practiceId: appt?.practiceId || resolvedPracticeId,
            practitionerId:
              appt?.practitionerId || appt?.userId || appt?.resourceId || null,
          },
        });
      } catch (error) {
        console.error("Error saving session:", error);
        setErrorMessage(
          "An error occurred while saving the session. Please try again.",
        );
      } finally {
        setIsSaving(false);
      }
    };

    const handleCancel = () => {
      setErrorMessage(false);
    };

    // Render methods
    const submitLoading = isSaving || (!adHoc ? false : !account && !practiceId);
    const handleSectionChange = async (key) => {
      // Capture write canvas while still visible before hiding it — exportImage fails on display:none
      if (localSection === "write" && key !== "write" && inlineWriteRef.current) {
        await inlineWriteRef.current.flush();
      }
      setLocalSection(key);
    };

    const handleExpand = async () => {
      // Capture canvas before fullscreen toggle — portaling remounts the tree, which resets the canvas
      if (inlineWriteRef.current) await inlineWriteRef.current.capture();
      setIsFullScreen((f) => !f);
    };

    const renderCurrentSection = () => {
      // Wrapped so a click event isn't destructured as the options object.
      const actionProps = { onSubmit: () => handleSubmit(), submitLoading, onExpand: handleExpand, isFullScreen, isMobile };

      switch (localSection) {
        case "apptNotepad":
          return (
            <NotepadSection
              notepadContent={notepadContent}
              setNotepadContent={setNotepadContent}
              onOpenNotepadRecorder={openRecorderForNotepad}
              isMobile={isMobile}
              {...actionProps}
            />
          );

        case "apptMedia":
          return (
            <MediaCaptureSection
              mediaImages={mediaImages}
              setMediaImages={setMediaImages}
              isActive={localSection === "apptMedia"}
              {...actionProps}
            />
          );

        default:
          return null; // apptTemplate rendered separately (always mounted)
      }
    };

    const navItems = [
      { key: "apptTemplate", label: "Template", icon: "fa-light fa-file-waveform" },
      { key: "apptNotepad",  label: "Note Pad", icon: "fa-light fa-clipboard-medical" },
      { key: "write",        label: "Write",    icon: "fa-light fa-pen-nib" },
      { key: "apptMedia",    label: "Media",    icon: "fa-light fa-camera" },
    ];

    const outerStyle = isFullScreen
      ? { position: "fixed", inset: 0, zIndex: 1049, background: "white", overflowY: "auto", display: "flex", flexDirection: "column" }
      : { marginTop: "4px" };

    const shortLabels = isLandscape || isNarrow;

    const sessionContent = (
      <div className="border" style={outerStyle}>
        <AppToast
          show={toast.show}
          onClose={hideToast}
          variant={toast.variant}
          message={toast.message}
        />
        <ConfirmModal {...confirmProps} />
        {successAlert && (
          <Alert variant="success" className="mb-0 rounded-0">
            The session was saved successfully and invoice created.
          </Alert>
        )}

        {/* setErrorMessage had two callers and no reader, so a save that failed
            on a missing patient/practice, or threw in the API, left the button
            looking like it had done nothing at all. The success case has been
            announced here all along; the failure case has to be too. */}
        {errorMessage && (
          <Alert
            variant="danger"
            className="mb-0 rounded-0"
            dismissible
            onClose={() => setErrorMessage(false)}
          >
            {errorMessage}
          </Alert>
        )}

        <div className={`d-flex flex-grow-1 ${isMobile ? "flex-column" : ""}`}>
          {/* Nav: top row on mobile, left column on desktop */}
          <div
            className={`d-flex gap-1 p-2 ${isMobile ? "flex-row flex-wrap border-bottom" : "flex-column border-end"}`}
            style={isMobile ? {} : { width: noteRail ? "210px" : "130px", flexShrink: 0 }}
          >
            {syncToProps && !hideInlineSyncTo && (
              <>
                <SyncToButton {...syncToProps} shortLabel={isMobile} />
                {isMobile && <hr className="my-2 mx-0" />}
              </>
            )}
            {/* The note list replaces the old section switcher: those four
                labels are kinds of note now, not tabs within one note. Falls
                back to the section nav for ad-hoc notes, which have no
                appointment to list notes against. */}
            {noteRail || navItems.map(({ key, label, icon }) => (
              <Button
                key={key}
                // Active rail item is the solid brand fill, inactive is the
                // outline — both straight off the Bootstrap variants, which
                // custom.scss pins to #0f3490. No inline colours needed.
                variant={localSection === key ? "primary" : "outline-primary"}
                className={`rounded-0 d-flex align-items-center gap-2${localSection === key ? " fw-semibold" : ""}`}
                onClick={() => handleSectionChange(key)}
              >
                <i className={icon} />
                {!isMobile && label}
              </Button>
            ))}
            {!isMobile && <hr className="my-2 mx-0" />}
            {!adHoc &&
              appointmentId &&
              appointmentStatus !== "Completed" &&
              appointmentStatus !== "New Patient" && (
                <Form.Check
                  type="checkbox"
                  id="followup-complete-appointment"
                  className="mb-1"
                  label="Complete appointment"
                  checked={completeAppointment}
                  onChange={(e) => setCompleteAppointment(e.target.checked)}
                />
              )}
            <Button
              variant="primary"
              className="rounded-0 d-flex align-items-center gap-2"
              onClick={() => handleSubmit()}
              disabled={isSaving}
            >
              <i className="fa-regular fa-floppy-disk" />
              {!isMobile && (isSaving ? "Processing..." : "Submit")}
            </Button>
          </div>

          {/* Section content */}
          <div className="p-2 flex-grow-1">
            {/* Write section always mounted so canvas state persists across section switches */}
            <div style={{ display: localSection === "write" ? "block" : "none" }}>
              <InlineWriteSection
                ref={inlineWriteRef}
                onSave={setHandwritingImage}
                existingImage={handwritingImage}
                onExpand={handleExpand}
                isFullScreen={isFullScreen}
              />
            </div>
            {/* Template section always mounted so ImageAnnotator canvas state persists */}
            <div style={{ display: localSection === "apptTemplate" ? "block" : "none" }}>
              <TemplateSection
                sessionTemplate={sessionTemplate}
                onTemplateChange={handleTemplateChange}
                dropdownOptions={dropdownOptions}
                templatePages={templatePages}
                capturedValues={capturedValues}
                onCapturedValuesChange={(elementId, value) => {
                  hasUserEditedRef.current = true;
                  setCapturedValues(prev => ({ ...prev, [elementId]: value }));
                }}
                onVoiceRequest={(elementId) => {
                  setVoiceTargetId(elementId);
                  setRecOpen(true);
                }}
                lastSaved={lastSaved}
                onExpand={handleExpand}
                isFullScreen={isFullScreen}
                isMobile={isMobile}
                isLandscape={window.innerWidth > window.innerHeight}
              />
            </div>
            {localSection !== "write" && localSection !== "apptTemplate" && renderCurrentSection()}
          </div>
        </div>

        {/* Handwriting modal — for full-screen edit via expand while on Write */}
        <HandwritingModal
          show={showHandwritingModal}
          onHide={() => setShowHandwritingModal(false)}
          onSave={setHandwritingImage}
          existingImage={handwritingImage}
        />

        <RecorderModal
          show={recOpen}
          onClose={closeRecorder}
          onDone={applyTranscriptToTarget}
          vr={vr}
        />

        {showRotateMessage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.85)",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              padding: "2rem",
              textAlign: "center",
            }}
          >
            <h5 className="text-white mb-2">Please rotate your device</h5>
            <p>This screen works best in landscape orientation.</p>
            {/* The only other way out of this overlay is a resize event that
                reports landscape. A narrow desktop window, an iPad in Split
                View, or an iOS resize that fires with stale dimensions never
                produces one — and with no control here the app looked frozen
                and could only be escaped by reloading. */}
            <Button
              variant="light"
              className="mt-3"
              onClick={() => resumeSubmitRef.current?.()}
            >
              Continue anyway
            </Button>
          </div>
        )}
      </div>
    );

    return isFullScreen
      ? createPortal(sessionContent, document.body)
      : sessionContent;
  },
);

export default FollowUpSession;
